import React from 'react';
import { Users, MessageSquare, TrendingUp, CheckCircle, type LucideIcon } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { apiService, WhatsAppStatus } from '../services/api';
import socketService from '../services/socket';
import type { Stats } from '../services/api';

const statCards: { label: string; key: keyof Stats; icon: LucideIcon; accent: 'purple' | 'blue' | 'green' | 'orange' }[] = [
  { label: 'Total Customers', key: 'totalCustomers', icon: Users, accent: 'purple' },
  { label: 'Linked to Trello', key: 'customersWithCards', icon: CheckCircle, accent: 'blue' },
  { label: 'Total Messages', key: 'totalMessages', icon: MessageSquare, accent: 'green' },
  { label: 'Last 24 Hours', key: 'recentMessages', icon: TrendingUp, accent: 'orange' },
];

export const DashboardPage: React.FC = () => {
  const { stats, customers, recentMessages, loading } = useDashboard();
  const [sessions, setSessions] = React.useState<WhatsAppStatus[]>([]);
  // Removed selectedSessionId and refreshTrigger logic as we only display read-only list here mostly
  // But we still need either polling or socket to keep list fresh. 

  const loadSessions = React.useCallback(async () => {
    try {
      const allSessions = await apiService.getAllSessions();
      setSessions(allSessions);
    } catch (e) { console.error(e); }
  }, []);

  React.useEffect(() => {
    loadSessions();
    const socket = socketService.connect();
    socket.on('whatsapp_status_update', () => loadSessions());
    socket.on('whatsapp_ready', () => loadSessions());
    // No need for QR updates here really, but no harm
    return () => { };
  }, [loadSessions]);

  // Removed handleCreateSession, handleLogoutSession, currentSession logic







  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  if (loading) {
    return (
      <div className="panel loading-panel">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="page-grid">
      <section className="panel stat-grid">
        {statCards.map(({ label, key, icon: Icon, accent }) => (
          <article key={label} className={`stat-chip accent-${accent}`}>
            <div className="stat-icon">
              <Icon size={22} />
            </div>
            <div>
              <p>{label}</p>
              <strong>{stats[key]}</strong>
            </div>
          </article>
        ))}
      </section>

      <section className="panel span-2">
        <header className="panel-header">
          <div>
            <p className="eyebrow">Customers</p>
            <h2>Last 10 Active Conversations</h2>
          </div>
          <span className="soft-chip">{customers.length} total</span>
        </header>
        <div className="table">
          <div className="table-head">
            <span>Customer</span>
            <span>Status</span>
            <span>Messages</span>
            <span>Last activity</span>
          </div>
          <div className="table-body">
            {customers.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No customers yet. Waiting for new conversations.</p>
              </div>
            ) : (
              customers.slice(0, 10).map((customer) => (
                <div key={customer._id} className="table-row">
                  <span>
                    <strong>{customer.name || 'Unknown contact'}</strong>
                    <p>{customer.phoneNumber}</p>
                    {customer.campaignName && <span className="pill muted" style={{ marginLeft: '0.5rem', fontSize: '0.7em' }}>Campaign: {customer.campaignName}</span>}
                  </span>
                  <span>
                    <span className={`pill ${customer.trelloCardId ? 'success' : 'muted'}`}>
                      {customer.trelloCardId ? 'Linked to Trello' : 'Unlinked'}
                    </span>
                  </span>
                  <span>{customer.metadata.totalMessages}</span>
                  <span>{formatDate(customer.lastContactDate)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <div>
            <p className="eyebrow">System Status</p>
            <h2>Connected Sessions</h2>
          </div>
        </header>

        <div className="sessions-list-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {sessions.length === 0 && <p className="text-secondary">No active sessions.</p>}
          {sessions.map((s, index) => (
            <div key={s.sessionId} className="session-item-row" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.8rem',
              background: 'var(--surface-muted)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600 }}>{s.phoneNumber || `Session ${index + 1}`}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{s.sessionId.substring(0, 8)}...</span>
              </div>
              <div className={`status-pill ${s.isReady ? 'success' : 'warning'}`} style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontWeight: 600,
                color: s.isReady ? 'var(--success)' : 'var(--warning)'
              }}>
                <span className={`status-dot ${s.isReady ? 'online' : 'offline'}`}></span>
                {s.isReady ? 'Online' : 'Offline'}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="panel span-2">
        <header className="panel-header">
          <div>
            <p className="eyebrow">Recent messages</p>
            <h2>Live feed</h2>
          </div>
        </header>
        <div className="message-feed">
          {recentMessages.length === 0 ? (
            <div className="empty-state">
              <MessageSquare size={48} />
              <p>No recent messages.</p>
            </div>
          ) : (
            recentMessages.map((message) => (
              <article key={message._id} className="message-card">
                <header>
                  <span className={`pill ${message.sender === 'customer' ? 'muted' : 'highlight'}`}>
                    {message.sender === 'customer' ? 'Customer' : 'Tamiyouz'}
                  </span>
                  <time>{formatDate(message.timestamp)}</time>
                </header>
                <p>{message.content}</p>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
};
